import { IMG } from "./images";

// Highlight pages for /highlights/:slug — showcase the experience, then point to tickets.
export const HIGHLIGHTS = [
  {
    slug: "underground",
    title: "The Underground (Hypogeum)",
    subtitle: "The hidden engine room of the arena, where the spectacle began.",
    heroImage: IMG.underground,
    gallery: [IMG.interiorArch, IMG.underground, IMG.interiorGolden],
    intro: "Beneath the arena floor lies the Hypogeum — a two-level network of tunnels, holding cells and lift shafts. Hidden from the crowd above, this was the backstage of the ancient world's greatest show.",
    blocks: [
      {
        type: "prose",
        heading: "A theatre of machinery",
        paragraphs: [
          "Some eighty vertical shafts once ran from the Hypogeum to the arena, fitted with counterweighted lifts and trapdoors. At a signal, wild animals and elaborate scenery could erupt onto the sand as if from nowhere — a feat of stagecraft that astonished spectators.",
          "Gladiators waited here in the gloom before climbing to face the crowd. The corridors still carry the marks of the wooden mechanisms, the channels for water, and the relentless logistics of staging life-and-death entertainment for tens of thousands.",
        ],
      },
      {
        type: "stats",
        items: [
          { value: "2", label: "Underground levels" },
          { value: "~80", label: "Lift & trapdoor shafts" },
          { value: "15+", label: "Animal holding areas" },
        ],
      },
      {
        type: "tips",
        heading: "What to expect on a visit",
        items: [
          { icon: "DoorOpen", title: "Restricted access", text: "Only reachable on a special guided tour — never with a standard ticket." },
          { icon: "Footprints", title: "Steps & tunnels", text: "Expect narrow, dimly lit passages and steps; not suitable for limited mobility." },
          { icon: "Clock", title: "Book early", text: "Daily capacity is tiny and tours sell out weeks ahead." },
        ],
      },
    ],
    relatedTickets: ["underground", "arena-floor", "guided-tours"],
    relatedHighlights: ["arena-floor", "architecture"],
    relatedPlanning: [
      { label: "Tips for Visiting", to: "/plan/tips" },
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
    ],
  },
  {
    slug: "arena-floor",
    title: "The Arena Floor",
    subtitle: "Stand at the centre of the amphitheatre, where gladiators met the crowd.",
    heroImage: IMG.interiorGolden,
    gallery: [IMG.interiorGolden, IMG.interiorGroup, IMG.interiorCrowd],
    intro: "The reconstructed arena floor recreates the stage where Rome's spectacles played out. Entering through the gladiators' gate and standing at its centre is among the most powerful moments any visitor can have here.",
    blocks: [
      {
        type: "prose",
        heading: "Where the sand once lay",
        paragraphs: [
          "The word 'arena' comes from the Latin for sand — harena — which was spread across the wooden floor to absorb the blood of combat. Beneath it, the Hypogeum hummed with activity, ready to deliver beasts and scenery into the light.",
          "Today a partial reconstruction lets you stand where the action unfolded, looking up at the tiers that once held 50,000 roaring spectators. From here, the sheer scale and acoustics of the building become unforgettable.",
        ],
      },
      {
        type: "quote",
        text: "To stand on the arena floor is to feel the weight of the crowd that once filled every tier above you.",
        author: "On visiting the Colosseum",
      },
      {
        type: "tips",
        heading: "Make the most of it",
        items: [
          { icon: "DoorOpen", title: "The Gate of Death", text: "Many tours enter via the Porta Libitinaria, used to carry the fallen from the sands." },
          { icon: "Camera", title: "Best photos", text: "Late afternoon light rakes across the tiers for dramatic images." },
          { icon: "Layers", title: "Pair with underground", text: "Underground tours usually include the floor — the ultimate combination." },
        ],
      },
    ],
    relatedTickets: ["arena-floor", "underground", "night-tours"],
    relatedHighlights: ["underground", "architecture"],
    relatedPlanning: [
      { label: "Entrances", to: "/plan/entrances" },
      { label: "Tips for Visiting", to: "/plan/tips" },
    ],
  },
  {
    slug: "roman-forum",
    title: "The Roman Forum",
    subtitle: "The political, religious and commercial heart of the ancient world.",
    heroImage: IMG.forumWide,
    gallery: [IMG.forumWide, IMG.forumColumns, IMG.forumAerial],
    intro: "Included with most Colosseum tickets, the Roman Forum was the stage on which the Roman Republic and Empire conducted their public life — from triumphal processions to fiery speeches and sacred rites.",
    blocks: [
      {
        type: "prose",
        heading: "Walking the Via Sacra",
        paragraphs: [
          "Follow the Via Sacra, the Forum's main thoroughfare, past the temples of Saturn and Vesta, the curving Senate House, and the triumphal arches of Titus and Septimius Severus. Each stone marks a chapter of Roman power.",
          "It takes imagination to rebuild the marble splendour from today's ruins, but a good guide or audio commentary brings the colonnades, statues and crowds vividly back to life.",
        ],
      },
      {
        type: "tips",
        heading: "Highlights to find",
        items: [
          { icon: "Columns", title: "Temple of Saturn", text: "Eight surviving columns mark Rome's ancient treasury." },
          { icon: "Flame", title: "House of the Vestals", text: "Home of the priestesses who guarded Rome's sacred flame." },
          { icon: "Landmark", title: "Arch of Titus", text: "The triumphal arch that inspired countless later monuments." },
        ],
      },
    ],
    relatedTickets: ["entry", "guided-tours", "combo"],
    relatedHighlights: ["palatine-hill", "architecture"],
    relatedPlanning: [
      { label: "Interactive Map", to: "/plan/map" },
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
    ],
  },
  {
    slug: "palatine-hill",
    title: "Palatine Hill",
    subtitle: "The cradle of Rome and the address of emperors, with the city's finest views.",
    heroImage: IMG.palatineRuins,
    gallery: [IMG.palatineRuins, IMG.palatinePillars, IMG.palatineAerial],
    intro: "Rising above the Forum, Palatine Hill is where, by legend, Romulus founded Rome — and where emperors later built sprawling palaces. It's also the most peaceful corner of the archaeological park, with breathtaking panoramas.",
    blocks: [
      {
        type: "prose",
        heading: "Where Rome began",
        paragraphs: [
          "The very word 'palace' derives from the Palatine, where Augustus and his successors built residences of staggering scale. Today you can wander the remains of the Domus Augustana and the stadium of Domitian among umbrella pines and wildflowers.",
          "Climb to the terraces for postcard views: the Forum laid out below on one side, the Circus Maximus on the other, and the Colosseum framed beyond. It's the ideal place to end an ancient-Rome itinerary.",
        ],
      },
      {
        type: "tips",
        heading: "Don't miss",
        items: [
          { icon: "Mountain", title: "The terraces", text: "The best free viewpoint over the Forum and Circus Maximus." },
          { icon: "Home", title: "Imperial palaces", text: "Trace the outlines of emperors' vast private residences." },
          { icon: "Trees", title: "The gardens", text: "The Farnese Gardens offer rare shade and calm." },
        ],
      },
    ],
    relatedTickets: ["entry", "guided-tours", "combo"],
    relatedHighlights: ["roman-forum", "architecture"],
    relatedPlanning: [
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
      { label: "Nearby Attractions", to: "/plan/nearby-attractions" },
    ],
  },
  {
    slug: "architecture",
    title: "Colosseum Architecture",
    subtitle: "The engineering masterpiece that has stood for nearly two thousand years.",
    heroImage: IMG.archDetail,
    gallery: [IMG.archDetail, IMG.columnClose, IMG.exteriorArches],
    intro: "The Colosseum is not just a relic — it's a textbook of Roman engineering. Its arches, vaults and crowd-flow design were so advanced that modern stadiums still echo its blueprint.",
    blocks: [
      {
        type: "prose",
        heading: "Built for the crowd",
        paragraphs: [
          "Completed around AD 80, the amphitheatre could hold an estimated 50,000 spectators and empty them through 80 entrances — the vomitoria — in minutes. Tiered seating reflected the rigid Roman social order, from senators ringside to commoners high above.",
          "Its three tiers of arches showcase the classical orders stacked in sequence — Doric, Ionic and Corinthian — while a vast retractable awning, the velarium, once shaded the audience, hauled into place by sailors of the Roman navy.",
        ],
      },
      {
        type: "stats",
        items: [
          { value: "189m", label: "Length of the oval" },
          { value: "80", label: "Entrance archways" },
          { value: "50,000", label: "Spectator capacity" },
        ],
      },
      {
        type: "tips",
        heading: "Look closely for",
        items: [
          { icon: "Columns", title: "Stacked orders", text: "Doric, Ionic and Corinthian columns rise tier by tier." },
          { icon: "Wind", title: "The velarium", text: "Sockets at the top once anchored the giant retractable awning." },
          { icon: "Hammer", title: "Travertine & tufa", text: "A clever mix of stone and early concrete made it strong and light." },
        ],
      },
    ],
    relatedTickets: ["guided-tours", "arena-floor", "entry"],
    relatedHighlights: ["underground", "arena-floor"],
    relatedPlanning: [
      { label: "Tips for Visiting", to: "/plan/tips" },
      { label: "Planning FAQs", to: "/plan/faqs" },
    ],
  },
];

export const getHighlight = (slug) => HIGHLIGHTS.find((h) => h.slug === slug);
